/**
 * Utilisation Forecast Report — full page, booking-driven, forward-looking.
 * Access Evo Design System compliant.
 *
 * "Booked"     = confirmed bookings starting on or after today.
 * "Planned"    = planned (unconfirmed) bookings starting on or after today.
 * "Projected"  = confirmed + planned combined.
 *
 * All metrics, charts and table rows exclusively reflect FUTURE bookings.
 * No historical data is included.
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';
import {
  format,
  addDays,
  addWeeks,
  addMonths,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,

  endOfDay,

  parseISO,
  getISOWeek,
  eachWeekOfInterval,
  isWithinInterval,
  isBefore,
  isAfter

} from 'date-fns';
import {
  TrendingUp,
  ChevronUp,
  ChevronDown,
  Download,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  SlidersHorizontal,
  Settings2,
  RotateCcw,
  GripVertical,
  X,
  Loader2,
  Users,
  FolderOpen,
  ArrowRight,
  Layers,
} from 'lucide-react';


import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpButton } from '@/components/help/HelpButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetHeader, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, Filter } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ComprehensiveBookingModal } from '@/components/calendar/ComprehensiveBookingModal';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useReportScope } from '@/hooks/useReportScope';
import {
  useForecastData,
  type ForecastRow,
  type ForecastType,
  type ForecastViewBy,
} from './useForecastData';

// ─── Colour token resolvers ────────────────────────────────────────────────────

// Recharts renders SVG <rect> elements whose `fill` attribute does not
// reliably support oklch colours in all browsers.  Convert CSS token values
// to an rgb() string that SVG can always render, caching the result.
const _svgColourCache = new Map<string, string>();
function resolveTokenForSvg(token: string, fallbackHex: string): string {
  const cached = _svgColourCache.get(token);
  if (cached) return cached;
  if (typeof window === 'undefined') return fallbackHex;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  if (!raw) { _svgColourCache.set(token, fallbackHex); return fallbackHex; }
  if (raw.startsWith('#') || raw.startsWith('rgb')) { _svgColourCache.set(token, raw); return raw; }
  const el = document.createElement('div');
  el.style.color = raw;
  document.body.appendChild(el);
  const rgb = getComputedStyle(el).color;
  el.remove();
  const result = rgb || fallbackHex;
  _svgColourCache.set(token, result);
  return result;
}

const TEAL         = () => resolveTokenForSvg('--teal-500',         '#14b8a6');
const AMBER        = () => resolveTokenForSvg('--warning-500',      '#EF9F27');
const RED          = () => resolveTokenForSvg('--destructive',      '#ef4444');
const GREY_DASHED  = () => resolveTokenForSvg('--muted-foreground', '#737373');

// ─── Project Cohort types ────────────────────────────────────────────────────

export type CohortEndStatus = 'confirmed' | 'planned' | 'at-risk';

export interface CohortRoleBreakdown {
  role: string;
  headcount: number;
  hours: number;
  keySkills: string[];
}

export interface ProjectCohortRow {
  projectId: string | number;
  projectName: string;
  clientName: string;
  endDate: string;        // ISO string
  weekLabel: string;      // e.g. "W19"
  resourcesReleasing: number;
  hoursReleasing: number;
  roles: CohortRoleBreakdown[];
  endStatus: CohortEndStatus;
  resources: import('@/types').Resource[];
}

export interface CohortWeekBar {
  weekLabel: string;
  weekStart: Date;
  confirmed: number;
  planned: number;
}

export interface CohortSummary {
  projectsEndingInPeriod: number;
  resourcesReleasing: number;
  totalHoursReleasing: number;
  openDemandHours: number;
  releasingCapacityHours: number;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SegmentedProps<T extends string> {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}
function Segmented<T extends string>({ options, value, onChange }: SegmentedProps<T>) {
  return (
    <div className="inline-flex items-center bg-muted rounded-md p-[3px] gap-[2px]">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={[
            'px-2.5 h-7 rounded text-xs font-medium transition-all whitespace-nowrap',
            o.value === value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

interface SortHeaderProps {
  label: string;
  col: string;
  sortCol: string;
  sortDir: 'asc' | 'desc';
  onSort: (col: string) => void;
  align?: 'left' | 'right';
  minWidth?: number;
}
function SortHeader({ label, col, sortCol, sortDir, onSort, align = 'left', minWidth }: SortHeaderProps) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="cursor-pointer select-none px-3 py-2.5 font-medium text-[12px] text-muted-foreground whitespace-nowrap group"
      style={{ textAlign: align, minWidth }}
    >
      <span
        className="inline-flex items-center gap-1"
        style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        {label}
        <span className={active ? 'text-foreground' : 'opacity-0 group-hover:opacity-50'}>
          {active && sortDir === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </span>
      </span>
    </th>
  );
}

function PctCell({ pct, targetPct }: { pct: number; targetPct: number }) {
  let colour = 'text-foreground';
  if (pct > 100) colour = 'text-destructive';
  else if (pct >= targetPct) colour = 'text-success-500';
  else if (pct >= targetPct - 10) colour = 'text-warning-500';
  else colour = 'text-destructive';
  return <span className={`${colour} font-medium`}>{pct}%</span>;
}

function VarianceCell({ variance }: { variance: number }) {
  const colour = variance >= 0 ? 'text-success-500' : 'text-destructive';
  const prefix = variance >= 0 ? '+' : '';
  return <span className={colour}>{prefix}{variance}%</span>;
}

function StatusPill({ status }: { status: ForecastRow['status'] }) {
  switch (status) {
    case 'on-target':
      return <Badge variant="success" accent="success" className="text-[11px] px-2 py-0.5">On track</Badge>;
    case 'below-target':
      return <Badge variant="warning" accent="warning" className="text-[11px] px-2 py-0.5">Under-booked</Badge>;
    case 'overloaded':
      return <Badge variant="destructive" accent="destructive" className="text-[11px] px-2 py-0.5">Over-capacity</Badge>;
    default:
      return <Badge variant="outline" className="text-[11px] px-2 py-0.5 text-muted-foreground">No forecast</Badge>;
  }
}

function BookingStatusPill({ status }: { status: 'confirmed' | 'planned' }) {
  if (status === 'confirmed') {
    return <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0 capitalize">Confirmed</Badge>;
  }
  return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground capitalize">Planned</Badge>;
}

type StatCardAccent = 'teal' | 'warning' | 'destructive' | 'default';

const accentClasses: Record<StatCardAccent, { value: string }> = {
  teal:        { value: 'text-[var(--accent-teal-900)] dark:text-[var(--accent-teal-500)]' },
  warning:     { value: 'text-[var(--accent-warning-900)] dark:text-[var(--accent-warning-500)]' },
  destructive: { value: 'text-destructive' },
  default:     { value: 'text-foreground' },
};

interface StatCardProps {
  value: string;
  accent?: StatCardAccent;
  label: string;
  subLabel: string;
  isFilterable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}
function StatCard({ value, accent = 'default', label, subLabel, isFilterable, isActive, onClick }: StatCardProps) {
  const classes = accentClasses[accent];
  return (
    <div
      className={[
        'flex-1 flex flex-col gap-1 rounded-[var(--radius-md)] border bg-card px-4 py-4 min-w-0 transition-all duration-150',
        isFilterable
          ? 'cursor-pointer select-none hover:border-primary/50 hover:shadow-sm'
          : '',
        isActive
          ? 'border-primary ring-2 ring-primary/20 shadow-sm'
          : 'border-border',
      ].join(' ')}
      role={isFilterable ? 'button' : undefined}
      tabIndex={isFilterable ? 0 : undefined}
      aria-pressed={isFilterable ? isActive : undefined}
      onClick={isFilterable ? onClick : undefined}
      onKeyDown={isFilterable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
    >
      <div className="flex items-start justify-between gap-1">
        <span className={`text-2xl font-semibold leading-none ${classes.value}`}>{value}</span>
        {isFilterable && isActive && (
          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </span>
        )}
      </div>
      <span className="text-xs text-muted-foreground mt-1">{label}</span>
      <span className="text-xs text-muted-foreground">{subLabel}</span>
      {isFilterable && !isActive && (
        <span className="text-[10px] text-primary/70 mt-1 font-medium">Click to filter ↓</span>
      )}
      {isFilterable && isActive && (
        <span className="text-[10px] text-primary mt-1 font-medium">Filtering active · click to clear</span>
      )}
    </div>
  );
}

function LegendSwatch({ colour, label, dashed }: { colour: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      {dashed ? (
        <svg width="16" height="4" className="shrink-0">
          <line x1="0" y1="2" x2="16" y2="2" stroke={colour} strokeWidth="1.5" strokeDasharray="3 2" />
        </svg>
      ) : (
        <span className="inline-block h-3 w-3 rounded-[2px] shrink-0" style={{ background: colour }} />
      )}
      {label}
    </span>
  );
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { target: number } }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const target    = payload[0]?.payload?.target ?? 75;
  const booked    = payload.find((p) => p.name === 'booked')?.value    ?? 0;
  const planned   = payload.find((p) => p.name === 'planned')?.value   ?? 0;
  const projected = booked + planned;
  const variance  = projected - target;
  return (
    <div
      className="rounded-md shadow-md text-[12px] p-3 flex flex-col gap-1 min-w-[185px]"
      style={{
        background: 'var(--popover)',
        border: '0.5px solid var(--border)',
        color: 'var(--popover-foreground)',
      }}
    >
      <p className="font-semibold mb-1">{label}</p>
      <p>Booked %: <strong>{booked}%</strong></p>
      <p>Planned %: <strong>{planned}%</strong></p>
      <p>Projected %: <strong>{projected}%</strong></p>
      <p>Target %: <strong>{target}%</strong></p>
      <p className={variance >= 0 ? 'text-success-500' : 'text-destructive'}>
        Variance: <strong>{variance >= 0 ? '+' : ''}{variance}%</strong>
      </p>
    </div>
  );
}

function bookedColourClass(bookedColour: string): { text: string; progressVar: string } {
  if (bookedColour.includes('destructive'))
    return { text: 'text-destructive', progressVar: 'var(--destructive)' };
  if (bookedColour.includes('warning'))
    return { text: 'text-warning-500', progressVar: 'var(--warning-500)' };
  return { text: 'text-teal-500', progressVar: 'var(--teal-500)' };
}

// ─── Detail drawer ────────────────────────────────────────────────────────────

function ForecastDrawer({
  row,
  open,
  onClose,
}: {
  row: ForecastRow | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!row) return null;

  const sparkData  = row.weeklyForecast.map((v, i) => ({ w: i, pct: v }));
  const colourCls  = bookedColourClass(row.bookedColour);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col gap-0 overflow-hidden w-full sm:max-w-[520px]"
      >
        <SheetTitle className="sr-only">{row.name} — Forecast Detail</SheetTitle>
        <SheetDescription className="sr-only">
          Projected utilisation breakdown and upcoming bookings for {row.name}.
        </SheetDescription>

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="h-11 w-11 shrink-0">
              <AvatarImage src={row.photoUrl} alt={row.name} />
              <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
                {row.initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-foreground truncate">{row.name}</p>
              <p className="text-xs text-muted-foreground truncate">{row.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 shrink-0 ml-3">
            <div className="text-right">
              <p className={`text-2xl font-semibold leading-none ${colourCls.text}`}>
                {row.bookedPct}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Booked</p>
            </div>
            <StatusPill status={row.status} />
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* At-risk alert panel */}
          {row.hasRiskFlag && (
            <div className="px-5 pt-4 pb-0">
              <Alert variant="warning" size="compact">
                <AlertDescription>
                  <span>
                    <strong>{row.name}</strong> is projected at{' '}
                    <strong>{row.riskPct}%</strong>, more than 10pp below the{' '}
                    <strong>{row.targetPct}%</strong> target.
                    {row.riskWeek ? ` ${row.riskWeek}.` : ''}
                    {row.gapHours > 0 && (
                      <> Needs <strong>{row.gapHours}h</strong> more bookings to reach target.</>
                    )}
                  </span>
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Section 1 — Forecast breakdown */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Projected utilisation breakdown</p>
            <div className="flex flex-col gap-3">
              {/* Booked bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Booked (confirmed)</span>
                  <span className={`font-semibold ${colourCls.text}`}>{row.bookedPct}%</span>
                </div>
                <div className="relative">
                  <Progress
                    value={Math.min(row.bookedPct, 100)}
                    className="h-2"
                    style={{ '--progress-color': colourCls.progressVar, background: 'var(--muted)' } as React.CSSProperties}
                  />
                  <div
                    className="absolute top-0 bottom-0 w-[2px] rounded opacity-60 bg-teal-500"
                    style={{ left: `${Math.min(row.targetPct, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Target: {row.targetPct}%</p>
              </div>

              {/* Projected bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Projected (confirmed + planned)</span>
                  <span className="font-semibold text-foreground">{row.projectedPct}%</span>
                </div>
                <Progress
                  value={Math.min(row.projectedPct, 100)}
                  className="h-2"
                  style={{ background: 'var(--muted)' } as React.CSSProperties}
                />
              </div>

              {/* Stat chips */}
              <div className="flex gap-2 mt-1">
                {[
                  { label: 'Confirmed hrs', value: `${Math.round(row.bookedHours)}h` },
                  { label: 'Planned hrs',   value: `${Math.round(row.plannedHours)}h` },
                  { label: 'Available hrs', value: `${Math.round(row.availableHours)}h` },
                ].map((chip) => (
                  <div
                    key={chip.label}
                    className="flex-1 rounded-md px-2.5 py-2 flex flex-col gap-0.5 text-center bg-muted border border-border"
                  >
                    <span className="text-sm font-semibold text-foreground">{chip.value}</span>
                    <span className="text-xs text-muted-foreground">{chip.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 2 — Upcoming bookings */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Upcoming bookings</p>
            {row.periodBookings.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No upcoming bookings in this forecast window.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {row.periodBookings.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-start justify-between gap-2 rounded-md border border-border px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{b.projectName}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {b.startDate} – {b.endDate}
                      </p>
                      <p className="text-xs text-muted-foreground">{b.allocationPct}% allocation</p>
                    </div>
                    <BookingStatusPill status={b.status} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 3 — 12-week forecast sparkline */}
          <div className="px-5 py-4">
            <p className="text-xs font-semibold text-foreground mb-3">
              Projected load — next 12 weeks
            </p>
            <div className="h-20 w-full overflow-hidden">
              <LineChart
                width={460}
                height={80}
                data={sparkData}
                margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
              >
                <ReferenceLine y={row.targetPct} stroke={TEAL()} strokeDasharray="3 2" strokeWidth={1} />
                <Line
                  type="monotone"
                  dataKey="pct"
                  stroke={TEAL()}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Dashed line = target ({row.targetPct}%) · Based on confirmed + planned bookings
            </p>
          </div>

        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Cohort sub-components ────────────────────────────────────────────────────

function CohortEndStatusPill({ status }: { status: CohortEndStatus }) {
  switch (status) {
    case 'confirmed':
      return <Badge variant="success" accent="success" className="text-[11px] px-2 py-0.5 whitespace-nowrap">Confirmed end</Badge>;
    case 'planned':
      return <Badge variant="warning" accent="warning" className="text-[11px] px-2 py-0.5 whitespace-nowrap">Planned end</Badge>;
    case 'at-risk':
      return <Badge variant="destructive" accent="destructive" className="text-[11px] px-2 py-0.5 whitespace-nowrap">At risk</Badge>;
  }
}

function CohortBarTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: CohortWeekBar }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload as CohortWeekBar | undefined;
  if (!row) return null;
  const confirmed = payload.find((p) => p.name === 'confirmed')?.value ?? 0;
  const planned   = payload.find((p) => p.name === 'planned')?.value   ?? 0;
  const total     = confirmed + planned;
  return (
    <div
      className="rounded-md shadow-md text-[12px] p-3 flex flex-col gap-1 min-w-[185px]"
      style={{
        background: 'var(--popover)',
        border: '0.5px solid var(--border)',
        color: 'var(--popover-foreground)',
      }}
    >
      <p className="font-semibold mb-1">{label}</p>
      <p>Resources releasing: <strong>{total}</strong></p>
      <p>Confirmed project ends: <strong>{confirmed}</strong></p>
      <p>Planned project ends: <strong>{planned}</strong></p>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div
        className="flex items-center justify-center h-14 w-14 rounded-2xl"
        style={{ background: 'var(--muted)' }}
      >
        <TrendingUp className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="text-[15px] font-medium text-foreground">
        No forecast data for the selected window
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs">
        No upcoming bookings match the selected filters. Try adjusting the forecast
        window or clearing the filters.
      </p>
      <button
        onClick={onReset}
        className="text-[13px] text-primary hover:underline mt-1 cursor-pointer"
      >
        Reset filters
      </button>
    </div>
  );
}

// ─── Date range display ───────────────────────────────────────────────────────

function formatDateRange(from: Date, to: Date): string {
  return `${format(from, 'dd MMM yyyy')} — ${format(to, 'dd MMM yyyy')}`;
}

// ─── Forward-looking period → date range ─────────────────────────────────────

type ForecastPeriodKey = 'next-2-weeks' | 'next-4-weeks' | 'next-month' | 'next-quarter';

function computeForecastRange(p: ForecastPeriodKey): { from: Date; to: Date } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = addDays(today, 1);
  switch (p) {
    case 'next-2-weeks':
      return { from: tomorrow, to: endOfDay(addDays(addWeeks(tomorrow, 2), -1)) };
    case 'next-4-weeks':
      return { from: tomorrow, to: endOfDay(addDays(addWeeks(tomorrow, 4), -1)) };
    case 'next-month':
      return {
        from: startOfMonth(addMonths(today, 1)),
        to:   endOfMonth(addMonths(today, 1)),
      };
    case 'next-quarter':
      return {
        from: startOfQuarter(addMonths(today, 3)),
        to:   endOfQuarter(addMonths(today, 3)),
      };
  }
}

function stepPeriod(
  currentFrom: Date,
  currentTo: Date,
  period: ForecastPeriodKey,
  direction: 'forward' | 'backward',
): { from: Date; to: Date } {
  const sign = direction === 'forward' ? 1 : -1;
  switch (period) {
    case 'next-2-weeks':
      return {
        from: addWeeks(currentFrom, 2 * sign),
        to:   addWeeks(currentTo,   2 * sign),
      };
    case 'next-4-weeks':
      return {
        from: addWeeks(currentFrom, 4 * sign),
        to:   addWeeks(currentTo,   4 * sign),
      };
    case 'next-month': {
      const newFrom = addMonths(currentFrom, sign);
      return { from: startOfMonth(newFrom), to: endOfMonth(newFrom) };
    }
    case 'next-quarter': {
      const newFrom = addMonths(currentFrom, 3 * sign);
      return { from: startOfQuarter(newFrom), to: endOfQuarter(newFrom) };
    }
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

export function UtilisationForecast() {
  const { canExportReport } = usePermissions();
  const canExport = canExportReport('utilisationForecastReport');
  const { resources: allResources, settings, schedulerGroups, projectDetails, bookings: allBookings, addBooking, updateBooking, deleteBooking } = useScreenData('calendar');

  // Scope visible resources by the role's report "View Access" (all / team / own)
  const { scopeResources } = useReportScope('utilisationForecastReport');
  const resources = useMemo(() => scopeResources(allResources), [scopeResources, allResources]);

  const defaultPeriod: ForecastPeriodKey = 'next-month';
  const { from: defaultFrom, to: defaultTo } = computeForecastRange(defaultPeriod);

  // ── Filter state
  const [dateFrom, setDateFrom] = useState<Date>(defaultFrom);
  const [dateTo,   setDateTo]   = useState<Date>(defaultTo);
  const [period,   setPeriod]   = useState<ForecastPeriodKey>(defaultPeriod);

  const handlePeriodChange = useCallback((p: ForecastPeriodKey) => {
    const { from, to } = computeForecastRange(p);
    setPeriod(p);
    setDateFrom(from);
    setDateTo(to);
    setPage(1);
  }, []);

  const handleStepPeriod = useCallback((direction: 'forward' | 'backward') => {
    const { from, to } = stepPeriod(dateFrom, dateTo, period, direction);
    setDateFrom(from);
    setDateTo(to);
    setPage(1);
  }, [dateFrom, dateTo, period]);

  const isPrevPeriodDisabled = useMemo(() => {
    const { from } = stepPeriod(dateFrom, dateTo, period, 'backward');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return isBefore(from, today);
  }, [dateFrom, dateTo, period]);

  const [viewBy,               setViewBy]               = useState<ForecastViewBy>('individual');
  const [forecastType,         setForecastType]         = useState<ForecastType>('both');
  const [selectedResources,    setSelectedResources]    = useState<string[]>([]);
  const [selectedDepartments,  setSelectedDepartments]  = useState<string[]>([]);
  const [selectedTeams,        setSelectedTeams]        = useState<string[]>([]);
  const [selectedRoles,        setSelectedRoles]        = useState<string[]>([]);
  const [filterDrawerOpen,     setFilterDrawerOpen]     = useState(false);

  // ── Project cohort specific state
  const [cohortMinSize,        setCohortMinSize]        = useState<number>(2);
  const [cohortProjectFilter,  setCohortProjectFilter]  = useState<string[]>([]);

  // ── Draft filter state (applied only on "Apply Filters" click)
  const [draftResources,    setDraftResources]    = useState<string[]>([]);
  const [draftDepartments,  setDraftDepartments]  = useState<string[]>([]);
  const [draftTeams,        setDraftTeams]        = useState<string[]>([]);
  const [draftRoles,        setDraftRoles]        = useState<string[]>([]);
  const [draftCohortMinSize,       setDraftCohortMinSize]       = useState<number>(2);
  const [draftCohortProjectFilter, setDraftCohortProjectFilter] = useState<string[]>([]);

  const openFilterDrawer = useCallback(() => {
    setDraftResources(selectedResources);
    setDraftDepartments(selectedDepartments);
    setDraftTeams(selectedTeams);
    setDraftRoles(selectedRoles);
    setDraftCohortMinSize(cohortMinSize);
    setDraftCohortProjectFilter(cohortProjectFilter);
    setFilterDrawerOpen(true);
  }, [selectedResources, selectedDepartments, selectedTeams, selectedRoles, cohortMinSize, cohortProjectFilter]);

  const applyFilters = useCallback(() => {
    setSelectedResources(draftResources);
    setSelectedDepartments(draftDepartments);
    setSelectedTeams(draftTeams);
    setSelectedRoles(draftRoles);
    setCohortMinSize(draftCohortMinSize);
    setCohortProjectFilter(draftCohortProjectFilter);
    setPage(1);
    setFilterDrawerOpen(false);
  }, [draftResources, draftDepartments, draftTeams, draftRoles, draftCohortMinSize, draftCohortProjectFilter]);

  const clearAllFilters = useCallback(() => {
    setDraftResources([]);
    setDraftDepartments([]);
    setDraftTeams([]);
    setDraftRoles([]);
    setDraftCohortMinSize(2);
    setDraftCohortProjectFilter([]);
    setSelectedResources([]);
    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedRoles([]);
    setCohortMinSize(2);
    setCohortProjectFilter([]);
    setPage(1);
    setFilterDrawerOpen(false);
  }, []);
  const [expandedCohortRows,   setExpandedCohortRows]   = useState<Set<string>>(new Set());
  const [bookingModalOpen,     setBookingModalOpen]     = useState(false);
  const [cohortPreselectedResources, setCohortPreselectedResources] = useState<import('@/types').Resource[]>([]);

  // ── Sort state
  const [sortCol, setSortCol] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ── Column visibility
  const [colConfigOpen, setColConfigOpen] = useState(false);
  type ForecastColKey = 'department' | 'target' | 'booked' | 'planned' | 'projected' | 'variance' | 'bookedh' | 'plannedh' | 'available';
  const ALL_FORECAST_COLS: { key: ForecastColKey; label: string; description: string }[] = [
    { key: 'department',  label: 'Department',    description: 'Resource department or planning group'  },
    { key: 'target',      label: 'Target %',      description: 'Target utilisation percentage'          },
    { key: 'booked',      label: 'Booked %',      description: 'Confirmed booking utilisation'          },
    { key: 'planned',     label: 'Planned %',     description: 'Planned (unconfirmed) booking utilisation' },
    { key: 'projected',   label: 'Projected %',   description: 'Confirmed + planned booking utilisation' },
    { key: 'variance',    label: 'Variance',      description: 'Difference from target'                 },
    { key: 'bookedh',     label: 'Booked hrs',    description: 'Total confirmed booking hours'          },
    { key: 'plannedh',    label: 'Planned hrs',   description: 'Total planned booking hours'            },
    { key: 'available',   label: 'Available hrs', description: 'Total available capacity hours'         },
  ];
  const [visibleForecastCols, setVisibleForecastCols] = useState<Set<ForecastColKey>>(
    new Set(['department','target','booked','planned','projected','variance','bookedh','plannedh','available'])
  );
  const toggleForecastCol = (key: ForecastColKey) => setVisibleForecastCols(prev => {
    const next = new Set(prev);
    if (next.has(key)) { if (next.size > 1) next.delete(key); } else next.add(key);
    return next;
  });
  const resetForecastCols = () => setVisibleForecastCols(new Set(['department','target','booked','planned','projected','variance','bookedh','plannedh','available']));

  // ── Pagination
  const [page,     setPage]     = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // ── Drawer
  const [drawerRow, setDrawerRow] = useState<ForecastRow | null>(null);

  // ── Card filter — 'on-track' = resources meeting target, 'near-target' = within 10pp, 'at-risk' = far below
  const [activeCardFilter, setActiveCardFilter] = useState<'on-track' | 'near-target' | 'at-risk' | null>(null);

  // ── Chart controls
  const [chartShowLimit, setChartShowLimit] = useState<number>(25);
  const [chartSortBy, setChartSortBy] = useState<
    'name' | 'booked-desc' | 'booked-asc' | 'projected-desc' | 'variance-desc'
  >('booked-desc');



  // ── Data
  const data = useForecastData(dateFrom, dateTo, viewBy, selectedResources, selectedDepartments, selectedTeams, selectedRoles);



  // ── Resource options
  const resourceOptions = useMemo(
    () =>
      resources
        .filter((r) => r.id !== 'unallocated')
        .map((r) => ({ value: r.id, label: r.name })),
    [resources],
  );

  const departmentOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const dept = r.department ?? '';
        if (dept && !seen.has(dept)) {
          seen.add(dept);
          acc.push({ value: dept, label: dept });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  // ── Planner Group options — only shown when grouping method is scheduler-group and groups are configured
  const teamsEnabled = settings.groupingMethod === 'scheduler-group' && schedulerGroups.length > 0;
  const teamOptions = useMemo(() => {
    if (!teamsEnabled) return [];
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated' && r.schedulerGroup)
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const team = r.schedulerGroup!;
        if (!seen.has(team)) {
          seen.add(team);
          acc.push({ value: team, label: team });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources, teamsEnabled]);

  const roleOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const role = r.role ?? '';
        if (role && !seen.has(role)) {
          seen.add(role);
          acc.push({ value: role, label: role });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  // ── Card-filtered rows — applied before sort/paginate and chart
  const cardFilteredRows = useMemo(() => {
    if (!activeCardFilter) return data.rows;
    if (activeCardFilter === 'on-track')  return data.rows.filter((r) => r.status === 'on-target');
    if (activeCardFilter === 'near-target') return data.rows.filter((r) => !r.hasRiskFlag && r.projectedPct < r.targetPct && (r.bookedHours > 0 || r.plannedHours > 0));
    if (activeCardFilter === 'at-risk')   return data.rows.filter((r) => r.hasRiskFlag);
    return data.rows;
  }, [data.rows, activeCardFilter]);

  const cardFilteredChartData = useMemo(() => {
    if (!activeCardFilter) return data.chartData;
    const nameSet = new Set(cardFilteredRows.map((r) => r.name));
    return data.chartData.filter((d) => nameSet.has(d.name));
  }, [data.chartData, cardFilteredRows, activeCardFilter]);

  // ── Chart display data
  const chartDisplayData = useMemo(() => {
    const sorted = [...cardFilteredChartData].sort((a, b) => {
      switch (chartSortBy) {
        case 'booked-desc':    return b.booked    - a.booked;
        case 'booked-asc':     return a.booked    - b.booked;
        case 'projected-desc': return b.projected - a.projected;
        case 'variance-desc':  return (b.booked - b.target) - (a.booked - a.target);
        case 'name':
        default:               return a.name.localeCompare(b.name);
      }
    });
    return chartShowLimit === -1 ? sorted : sorted.slice(0, chartShowLimit);
  }, [cardFilteredChartData, chartSortBy, chartShowLimit]);



  // ── Sort + paginate
  const sortedRows = useMemo(() => {
    return [...cardFilteredRows].sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      switch (sortCol) {
        case 'name':       va = a.name;           vb = b.name;           break;
        case 'department': va = a.department;     vb = b.department;     break;
        case 'target':     va = a.targetPct;      vb = b.targetPct;      break;
        case 'booked':     va = a.bookedPct;      vb = b.bookedPct;      break;
        case 'planned':    va = a.plannedPct;     vb = b.plannedPct;     break;
        case 'projected':  va = a.projectedPct;   vb = b.projectedPct;   break;
        case 'variance':   va = a.variancePct;    vb = b.variancePct;    break;
        case 'bookedh':    va = a.bookedHours;    vb = b.bookedHours;    break;
        case 'plannedh':   va = a.plannedHours;   vb = b.plannedHours;   break;
        case 'available':  va = a.availableHours; vb = b.availableHours; break;
        default:           va = a.name;           vb = b.name;
      }
      if (typeof va === 'string') {
        return sortDir === 'asc'
          ? va.localeCompare(vb as string)
          : (vb as string).localeCompare(va);
      }
      return sortDir === 'asc'
        ? (va as number) - (vb as number)
        : (vb as number) - (va as number);
    });
  }, [cardFilteredRows, sortCol, sortDir]);

  const totalPages   = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = useCallback(
    (col: string) => {
      if (col === sortCol) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortCol(col);
        setSortDir('asc');
      }
      setPage(1);
    },
    [sortCol],
  );

  const handleReset = useCallback(() => {
    const { from, to } = computeForecastRange(defaultPeriod);
    setDateFrom(from);
    setDateTo(to);
    setPeriod(defaultPeriod);
    setViewBy('individual');
    setForecastType('both');
    setSelectedResources([]);
    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedRoles([]);
    setSortCol('name');
    setSortDir('asc');
    setPage(1);
    setChartShowLimit(25);
    setChartSortBy('booked-desc');
    setActiveCardFilter(null);
    setCohortMinSize(2);
    setCohortProjectFilter([]);
    setExpandedCohortRows(new Set());
  }, []);

  // ── Export
  const handleExportExcel = useCallback(() => {
    if (!data.rows.length) {
      toast.error('Nothing to export', { description: 'No forecast data matches the current filters.' });
      return;
    }

    const dateLabel = `${format(dateFrom, 'dd MMM yyyy')} – ${format(dateTo, 'dd MMM yyyy')}`;

    const summaryRows = data.rows.map((r) => ({
      'Resource':          r.name,
      'Role':              r.role,
      'Department':        r.department,
      'Forecast Window':   dateLabel,
      'Target %':          r.targetPct,
      'Booked %':          r.bookedPct,
      'Planned %':         r.plannedPct,
      'Projected %':       r.projectedPct,
      'Variance %':        r.variancePct,
      'Booked Hours':      Math.round(r.bookedHours),
      'Planned Hours':     Math.round(r.plannedHours),
      'Available Hours':   Math.round(r.availableHours),
      'Forecast Status':   r.status.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      'Capacity Risk':     r.hasRiskFlag
        ? `Yes — projected ${r.riskPct ?? ''}% in ${r.riskWeek ?? ''}`
        : 'No',
    }));

    const bookingRows = data.rows.flatMap((r) =>
      r.periodBookings.map((b) => ({
        'Resource':     r.name,
        'Project':      b.projectName,
        'Start Date':   b.startDate,
        'End Date':     b.endDate,
        'Allocation %': b.allocationPct,
        'Status':       b.status.charAt(0).toUpperCase() + b.status.slice(1),
      })),
    );

    const wb = XLSX.utils.book_new();

    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
    wsSummary['!cols'] = Object.keys(summaryRows[0]).map((k) => ({
      wch:
        Math.max(
          k.length,
          ...summaryRows.map((r) => String(r[k as keyof typeof r]).length),
        ) + 2,
    }));
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Utilisation Forecast');

    if (bookingRows.length) {
      const wsBookings = XLSX.utils.json_to_sheet(bookingRows);
      wsBookings['!cols'] = Object.keys(bookingRows[0]).map((k) => ({
        wch:
          Math.max(
            k.length,
            ...bookingRows.map((r) => String(r[k as keyof typeof r]).length),
          ) + 2,
      }));
      XLSX.utils.book_append_sheet(wb, wsBookings, 'Upcoming Bookings');
    }

    const filename = `utilisation-forecast-${format(dateFrom, 'yyyy-MM-dd')}-to-${format(dateTo, 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(wb, filename);

    toast.success('Export complete', {
      description: `${data.rows.length} resource${data.rows.length !== 1 ? 's' : ''} exported to Excel.`,
    });
  }, [data.rows, dateFrom, dateTo]);

  // Footer totals
  const footerTotals = useMemo(() => {
    const rows = data.rows;
    if (rows.length === 0) return null;
    const activeRows = rows.filter((r) => r.bookedHours > 0 || r.plannedHours > 0);
    return {
      targetAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.targetPct, 0) / activeRows.length)
          : 0,
      bookedAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.bookedPct, 0) / activeRows.length)
          : 0,
      plannedAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.plannedPct, 0) / activeRows.length)
          : 0,
      projectedAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.projectedPct, 0) / activeRows.length)
          : 0,
      varianceAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.variancePct, 0) / activeRows.length)
          : 0,
      bookedSum:    Math.round(rows.reduce((s, r) => s + r.bookedHours,    0)),
      plannedSum:   Math.round(rows.reduce((s, r) => s + r.plannedHours,   0)),
      availableSum: Math.round(rows.reduce((s, r) => s + r.availableHours, 0)),
    };
  }, [data.rows]);

  // ── Project Cohort data ────────────────────────────────────────────────────

  // Derive project-cohort options from projectDetails for the filter multiselect
  const cohortProjectOptions = useMemo(() => {
    return projectDetails
      .filter((p) => p.endDate)
      .map((p) => ({ value: String(p.id), label: p.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [projectDetails]);

  // Build cohort rows from projectDetails ending within the forecast window
  const cohortRows = useMemo((): ProjectCohortRow[] => {
    if (viewBy !== 'project-cohort') return [];

    const rows: ProjectCohortRow[] = [];

    for (const project of projectDetails) {
      if (!project.endDate) continue;

      const endDate = parseISO(project.endDate);
      // Check if end date falls within the forecast window
      if (isBefore(endDate, dateFrom) || isAfter(endDate, dateTo)) continue;

      // Filter to specific projects if filter is active
      if (cohortProjectFilter.length > 0 && !cohortProjectFilter.includes(String(project.id))) continue;

      // Find resources on this project (via bookings that reference this project)
      const projectBookings = allBookings.filter((b) => {
        if (b.status === 'cancelled') return false;
        const bProjectName = b.projectName ?? '';
        return bProjectName === project.name || String(b.projectId ?? '') === String(project.id);
      });

      const resourceIds = new Set<string>();
      for (const b of projectBookings) {
        if (b.resourceId && b.resourceId !== 'unallocated') resourceIds.add(b.resourceId);
        if (b.resourceIds) {
          for (const rid of b.resourceIds) {
            if (rid && rid !== 'unallocated') resourceIds.add(rid);
          }
        }
      }

      const releasingResources = resources.filter(
        (r) => r.id !== 'unallocated' && r.status !== 'inactive' && resourceIds.has(r.id),
      );

      if (releasingResources.length < cohortMinSize) continue;

      // Approximate hours releasing = avg 40h per resource per week for 1 week
      // Use a simple calculation: available hours in the release week for each resource
      const approxHoursPerResource = 40;
      const hoursReleasing = releasingResources.length * approxHoursPerResource;

      // Build role breakdown
      const roleMap = new Map<string, { headcount: number; hours: number; skills: Set<string> }>();
      for (const r of releasingResources) {
        const role = r.role ?? 'Unknown';
        if (!roleMap.has(role)) roleMap.set(role, { headcount: 0, hours: 0, skills: new Set() });
        const entry = roleMap.get(role)!;
        entry.headcount += 1;
        entry.hours += approxHoursPerResource;
        if (r.skills) {
          for (const s of r.skills) entry.skills.add(s.name);
        }
      }

      const roles: CohortRoleBreakdown[] = Array.from(roleMap.entries())
        .sort((a, b) => b[1].headcount - a[1].headcount)
        .map(([role, { headcount, hours, skills }]) => ({
          role,
          headcount,
          hours,
          keySkills: Array.from(skills).slice(0, 3),
        }));

      // Determine end status based on project status
      let endStatus: CohortEndStatus = 'confirmed';
      const pStatus = (project.status ?? '').toLowerCase();
      if (pStatus === 'on hold' || pStatus === 'cancelled') {
        endStatus = 'at-risk';
      } else if (pStatus === 'planning') {
        endStatus = 'planned';
      }

      const weekNum = getISOWeek(endDate);

      rows.push({
        projectId:          project.id,
        projectName:        project.name,
        clientName:         project.clientName ?? '—',
        endDate:            project.endDate,
        weekLabel:          `W${weekNum}`,
        resourcesReleasing: releasingResources.length,
        hoursReleasing,
        roles,
        endStatus,
        resources:          releasingResources,
      });
    }

    // Sort by end date ascending
    rows.sort((a, b) => a.endDate.localeCompare(b.endDate));
    return rows;
  }, [viewBy, projectDetails, dateFrom, dateTo, allBookings, resources, cohortMinSize, cohortProjectFilter]);

  // Build week bars for the cohort bar chart
  const cohortWeekBars = useMemo((): CohortWeekBar[] => {
    if (viewBy !== 'project-cohort') return [];
    const weeks = eachWeekOfInterval({ start: dateFrom, end: dateTo }, { weekStartsOn: 1 });
    return weeks.map((weekStart) => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const weekLabel = `W${getISOWeek(weekStart)}`;
      let confirmed = 0;
      let planned = 0;
      for (const row of cohortRows) {
        const ed = parseISO(row.endDate);
        if (isWithinInterval(ed, { start: weekStart, end: weekEnd })) {
          if (row.endStatus === 'confirmed') confirmed += row.resourcesReleasing;
          else planned += row.resourcesReleasing;
        }
      }
      return { weekLabel, weekStart, confirmed, planned };
    });
  }, [viewBy, cohortRows, dateFrom, dateTo]);

  // Build cohort summary
  const cohortSummary = useMemo((): CohortSummary => {
    const uniqueResources = new Set<string>();
    for (const row of cohortRows) {
      for (const r of row.resources) uniqueResources.add(r.id);
    }
    const totalHoursReleasing = cohortRows.reduce((s, r) => s + r.hoursReleasing, 0);
    // Open demand: approximate from bookings that don't have a resource yet (unallocated)
    const openDemandHours = allBookings
      .filter((b) => {
        if (b.status === 'cancelled') return false;
        if (b.resourceId !== 'unallocated' && b.resourceId) return false;
        const bStart = parseISO(b.startTime);
        const bEnd   = parseISO(b.endTime);
        return bStart < dateTo && bEnd > dateFrom;
      })
      .length * 40; // approximate 40h per unallocated booking

    return {
      projectsEndingInPeriod: cohortRows.length,
      resourcesReleasing:     uniqueResources.size,
      totalHoursReleasing,
      openDemandHours,
      releasingCapacityHours: totalHoursReleasing,
    };
  }, [cohortRows, allBookings, dateFrom, dateTo]);

  // Bar colour logic — both bars use projected (booked + planned) status so
  // at-risk / near-target colours are always visible, even when booked is 0.
  function resolveBarBase(entry: { booked: number; planned: number; target: number }): string {
    const projected = entry.booked + entry.planned;
    if (projected > 100)              return RED();
    if (projected >= entry.target)    return TEAL();
    if (projected >= entry.target - 10) return AMBER();
    return RED();
  }
  function barOpacityBooked(): number {
    return forecastType === 'planned' ? 0.4 : 1;
  }
  function barOpacityPlanned(): number {
    return forecastType === 'booked' ? 0.2 : 0.4;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-6 py-4 shrink-0"
        style={{ borderBottom: '0.5px solid var(--color-border-tertiary, var(--border))' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <TrendingUp className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground leading-tight">
              Utilisation forecast
            </h1>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Projected utilisation — forward-looking, based on confirmed and planned bookings only
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <FeatureFeedbackCollector feature="Utilisation Forecast" />
          <HelpButton />
        </div>
      </div>

      {/* ── Filters bar ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 py-3 shrink-0"
        style={{
          background: 'var(--color-background-secondary, var(--muted))',
          borderBottom: '0.5px solid var(--color-border-tertiary, var(--border))',
        }}
      >
        {/* Forecast window selector */}
        <Select value={period} onValueChange={(v) => handlePeriodChange(v as ForecastPeriodKey)}>
          <SelectTrigger className="h-8 text-[13px] w-auto min-w-[150px] shrink-0 bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="next-2-weeks">Next 2 weeks</SelectItem>
            <SelectItem value="next-4-weeks">Next 4 weeks</SelectItem>
            <SelectItem value="next-month">Next month</SelectItem>
            <SelectItem value="next-quarter">Next quarter</SelectItem>
          </SelectContent>
        </Select>

        {/* Prev / Next arrows */}
        <div className="inline-flex items-center gap-0.5 shrink-0">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-background"
            onClick={() => handleStepPeriod('backward')}
            disabled={isPrevPeriodDisabled}
            aria-label="Previous period"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-background"
            onClick={() => handleStepPeriod('forward')}
            aria-label="Next period"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Date range display */}
        <div
          className="inline-flex items-center h-8 px-3 rounded-md bg-background text-[13px] text-muted-foreground shrink-0"
          style={{ border: '1px solid var(--input)' }}
        >
          {formatDateRange(dateFrom, dateTo)}
        </div>

        {/* View by */}
        <Select
          value={viewBy}
          onValueChange={(v) => {
            setViewBy(v as ForecastViewBy);
            setPage(1);
            setSortCol('name');
            setSortDir('asc');
            setExpandedCohortRows(new Set());
          }}
        >
          <SelectTrigger className="h-8 text-[13px] w-auto min-w-[150px] shrink-0 bg-background">
            <SelectValue placeholder="View by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Individual</SelectItem>
            <SelectItem value="planning-group">Team</SelectItem>
            <SelectItem value="department">Department</SelectItem>
            <SelectItem value="location">Location</SelectItem>
            <SelectItem value="role">Role</SelectItem>
            <SelectItem value="project-cohort">Project cohort</SelectItem>
          </SelectContent>
        </Select>

        {/* Forecast type */}
        <Segmented
          options={[
            { label: 'Booked',     value: 'booked' },
            { label: 'Planned',    value: 'planned' },
            { label: 'Projected',  value: 'both' },
          ]}
          value={forecastType}
          onChange={(v) => setForecastType(v as ForecastType)}
        />

        <div className="ml-auto flex items-center gap-2">
          {(selectedResources.length > 0 || selectedDepartments.length > 0 || selectedTeams.length > 0 || selectedRoles.length > 0 || cohortProjectFilter.length > 0 || cohortMinSize !== 2) && (
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <X className="h-3 w-3" />
              Reset
            </button>
          )}
          {/* Filter button */}
          <Button
            variant="outline"
            size="icon"
            className="relative h-8 w-8 shrink-0 bg-background"
            onClick={openFilterDrawer}
            aria-label="Open filters"
          >
            <Filter className="h-4 w-4" />
            {(() => {
              const count =
                (selectedResources.length > 0 ? 1 : 0) +
                (selectedDepartments.length > 0 ? 1 : 0) +
                (selectedTeams.length > 0 ? 1 : 0) +
                (selectedRoles.length > 0 ? 1 : 0) +
                (cohortProjectFilter.length > 0 ? 1 : 0) +
                (cohortMinSize !== 2 ? 1 : 0);
              return count > 0 ? (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                  {count}
                </span>
              ) : null;
            })()}
          </Button>

          {/* Column config button */}
          <Button
            variant="outline"
            size="icon"
            className="relative h-8 w-8 shrink-0 bg-background"
            onClick={() => setColConfigOpen(true)}
            aria-label="Configure columns"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {visibleForecastCols.size < ALL_FORECAST_COLS.length && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                {ALL_FORECAST_COLS.length - visibleForecastCols.size}
              </span>
            )}
          </Button>

          {/* Export to Excel */}
          {canExport && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0 bg-background"
                  onClick={handleExportExcel}
                  aria-label="Export utilisation forecast to Excel"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export to Excel</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          )}
        </div>
      </div>

      {/* ── Column config sheet ─────────────────────────────────────────── */}
      <Sheet open={colConfigOpen} onOpenChange={setColConfigOpen}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Configure columns</SheetTitle>
          <SheetDescription className="sr-only">Toggle columns on or off for the utilisation forecast table.</SheetDescription>
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold leading-tight">Customise Columns</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Toggle columns on or off to personalise the table.</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">{visibleForecastCols.size}</div>
                <span className="text-sm text-muted-foreground">of {ALL_FORECAST_COLS.length} columns showing</span>
              </div>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={resetForecastCols}>
                <RotateCcw className="h-3.5 w-3.5" />Reset defaults
              </Button>
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-2">
                {ALL_FORECAST_COLS.map((col) => {
                  const isVisible = visibleForecastCols.has(col.key);
                  return (
                    <div key={col.key} className={['flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150', isVisible ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30' : 'bg-muted/10 border-border/30'].join(' ')}>
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/30" />
                      <div className="flex-1 min-w-0">
                        <p className={['text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground'].join(' ')}>{col.label}</p>
                        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">{col.description}</p>
                      </div>
                      <Switch checked={isVisible} onCheckedChange={() => toggleForecastCol(col.key)} aria-label={`${isVisible ? 'Hide' : 'Show'} ${col.label} column`} />
                    </div>
                  );
                })}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          <SheetFooter className="px-4 py-3 border-t shrink-0">
            <Button className="w-full" onClick={() => setColConfigOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Filter drawer ─────────────────────────────────────────────────── */}
      <Sheet open={filterDrawerOpen} onOpenChange={(open) => { if (!open) setFilterDrawerOpen(false); }}>
        <SheetContent side="right" className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Utilisation forecast filters</SheetTitle>
          <SheetDescription className="sr-only">Filter by resource, department, planning group and role.</SheetDescription>
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Filters</span>
              {(() => {
                const count = (draftResources.length > 0 ? 1 : 0) + (draftDepartments.length > 0 ? 1 : 0) + (draftTeams.length > 0 ? 1 : 0) + (draftRoles.length > 0 ? 1 : 0) + (draftCohortProjectFilter.length > 0 ? 1 : 0);
                return (
                  <Badge variant="secondary" className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10">
                    <Filter className="h-3 w-3" />
                    {count} {count === 1 ? 'filter' : 'filters'} active
                  </Badge>
                );
              })()}
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">
                {viewBy !== 'project-cohort' && (
                  <>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Resource</label>
                      <MultiSelect options={resourceOptions} placeholder="All resources" value={draftResources} onValueChange={setDraftResources} maxDisplay={2} />
                    </div>
                    <Separator />
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Department</label>
                      <MultiSelect options={departmentOptions} placeholder="All departments" value={draftDepartments} onValueChange={setDraftDepartments} maxDisplay={2} />
                    </div>
                    {teamsEnabled && (
                      <>
                        <Separator />
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-foreground">Planning Group</label>
                          <MultiSelect options={teamOptions} placeholder="All planning groups" value={draftTeams} onValueChange={setDraftTeams} maxDisplay={2} />
                        </div>
                      </>
                    )}
                    <Separator />
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Role</label>
                      <MultiSelect options={roleOptions} placeholder="All roles" value={draftRoles} onValueChange={setDraftRoles} maxDisplay={2} />
                    </div>
                  </>
                )}
                {viewBy === 'project-cohort' && (
                  <>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Minimum cohort size</label>
                      <p className="text-[11px] text-muted-foreground">Show projects releasing at least this many resources</p>
                      <Select value={String(draftCohortMinSize)} onValueChange={(v) => setDraftCohortMinSize(Number(v))}>
                        <SelectTrigger className="h-8 text-[13px] bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2">2+ resources</SelectItem>
                          <SelectItem value="5">5+ resources</SelectItem>
                          <SelectItem value="10">10+ resources</SelectItem>
                          <SelectItem value="20">20+ resources</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Separator />
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Projects</label>
                      <MultiSelect
                        options={cohortProjectOptions}
                        placeholder="All projects"
                        value={draftCohortProjectFilter}
                        onValueChange={setDraftCohortProjectFilter}
                        maxDisplay={2}
                      />
                    </div>
                  </>
                )}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={clearAllFilters}
              disabled={(draftResources.length + draftDepartments.length + draftTeams.length + draftRoles.length + draftCohortProjectFilter.length) === 0}
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </Button>
            <Button className="h-9 gap-1.5 px-5" onClick={applyFilters}>
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Scrollable content area ───────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
        {data.isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading forecast data…</p>
          </div>
        ) : data.isEmpty ? (
          <EmptyState onReset={handleReset} />
        ) : viewBy === 'project-cohort' ? (
          <>
            {/* ╝╝ PROJECT COHORT VIEW ╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝ */}

            {/* ── Cohort stat cards ─────────────────────────────────────── */}
            <div className="flex gap-3">
              <StatCard
                value={String(cohortSummary.projectsEndingInPeriod)}
                label="Projects ending in period"
                subLabel="Projects with end dates within the forecast window"
              />
              <StatCard
                value={String(cohortSummary.resourcesReleasing)}
                accent="teal"
                label="Resources releasing"
                subLabel="Total headcount becoming free as projects end"
              />
              <StatCard
                value={`${cohortSummary.totalHoursReleasing}h`}
                label="Total hours releasing"
                subLabel="Sum of available hours across all releasing resources"
              />
              <StatCard
                value={
                  cohortSummary.releasingCapacityHours > 0
                    ? `${Math.round((cohortSummary.openDemandHours / cohortSummary.releasingCapacityHours) * 100)}%`
                    : '—'
                }
                accent={
                  cohortSummary.releasingCapacityHours >= cohortSummary.openDemandHours
                    ? 'teal'
                    : 'destructive'
                }
                label="Open demand vs releasing capacity"
                subLabel={
                  cohortSummary.releasingCapacityHours >= cohortSummary.openDemandHours
                    ? `${cohortSummary.openDemandHours}h demand · ${cohortSummary.releasingCapacityHours}h capacity · surplus`
                    : `${cohortSummary.openDemandHours}h demand · ${cohortSummary.releasingCapacityHours}h capacity · shortfall`
                }
              />
            </div>

            {/* ── Cohort action row ─────────────────────────────────────── */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-[13px] border-teal-500 text-teal-600 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-950"
                onClick={() => {
                  const allResources = cohortRows.flatMap((r) => r.resources);
                  const unique = Array.from(new Map(allResources.map((r) => [r.id, r])).values());
                  if (unique.length === 0) {
                    toast.error('No releasing resources', { description: 'No resources are releasing in the current filtered view.' });
                    return;
                  }
                  setCohortPreselectedResources(unique);
                  setBookingModalOpen(true);
                }}
              >
                <Users className="h-3.5 w-3.5" />
                Assign all releasing resources
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-[13px]"
                onClick={() => {
                  if (cohortRows.length === 0) {
                    toast.error('Nothing to export', { description: 'No cohort data matches the current filters.' });
                    return;
                  }
                  const rows = cohortRows.map((r) => ({
                    'Project':             r.projectName,
                    'Client':              r.clientName,
                    'End Date':            format(parseISO(r.endDate), 'dd MMM yyyy'),
                    'Week Releasing':      r.weekLabel,
                    'Resources Releasing': r.resourcesReleasing,
                    'Hours Releasing':     r.hoursReleasing,
                    'Roles':               r.roles.map((rl) => `${rl.headcount} ${rl.role}`).join(' · '),
                    'Status':              r.endStatus === 'confirmed' ? 'Confirmed end' : r.endStatus === 'planned' ? 'Planned end' : 'At risk',
                  }));
                  const wb = XLSX.utils.book_new();
                  const ws = XLSX.utils.json_to_sheet(rows);
                  ws['!cols'] = Object.keys(rows[0]).map((k) => ({
                    wch: Math.max(k.length, ...rows.map((r) => String(r[k as keyof typeof r]).length)) + 2,
                  }));
                  XLSX.utils.book_append_sheet(wb, ws, 'Project Cohort');
                  XLSX.writeFile(wb, `project-cohort-${format(dateFrom, 'yyyy-MM-dd')}-to-${format(dateTo, 'yyyy-MM-dd')}.xlsx`);
                  toast.success('Export complete', { description: `${cohortRows.length} project${cohortRows.length !== 1 ? 's' : ''} exported.` });
                }}
              >
                <Download className="h-3.5 w-3.5" />
                Export cohort list
              </Button>
            </div>

            {/* ── Releasing headcount by week chart ─────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)] p-4 flex flex-col gap-3"
              style={{
                background: 'var(--background)',
                border: '0.5px solid var(--color-border-tertiary, var(--border))',
              }}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-[13px] font-medium text-foreground">
                    Releasing headcount by week
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    · {cohortRows.length} project{cohortRows.length !== 1 ? 's' : ''} across {cohortWeekBars.filter((b) => b.confirmed + b.planned > 0).length} week{cohortWeekBars.filter((b) => b.confirmed + b.planned > 0).length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {cohortWeekBars.length === 0 ? (
                <p className="text-[13px] text-muted-foreground py-8 text-center">
                  No projects ending within this forecast window.
                </p>
              ) : (
                <>
                  <div style={{ width: '100%', height: 220 }}>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={cohortWeekBars}
                        margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                        barCategoryGap="30%"
                        barGap={2}
                      >
                        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis
                          dataKey="weekLabel"
                          tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                          axisLine={false}
                          tickLine={false}
                          width={36}
                          allowDecimals={false}
                          label={{ value: 'Resources', angle: -90, position: 'insideLeft', fontSize: 10, fill: 'var(--muted-foreground)' }}
                        />
                        <RechartsTooltip content={<CohortBarTooltip />} cursor={{ fill: 'var(--accent)', opacity: 0.3 }} />
                        <Bar dataKey="confirmed" name="confirmed" stackId="a" radius={[0, 0, 0, 0]} fill={TEAL()} maxBarSize={40} />
                        <Bar dataKey="planned"   name="planned"   stackId="a" radius={[3, 3, 0, 0]} fill={AMBER()} maxBarSize={40} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 px-1">
                    <LegendSwatch colour={TEAL()}  label="Confirmed project end" />
                    <LegendSwatch colour={AMBER()} label="Planned / tentative end" />
                  </div>
                </>
              )}
            </div>

            {/* ── Project cohort table ──────────────────────────────────── */}
            {cohortRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-2xl" style={{ background: 'var(--muted)' }}>
                  <FolderOpen className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-[14px] font-medium text-foreground">No projects ending in this window</p>
                <p className="text-[13px] text-muted-foreground max-w-xs">
                  No projects with end dates in the selected forecast window match the current filters.
                  Try a longer window or adjusting the minimum cohort size.
                </p>
              </div>
            ) : (
              <div
                className="rounded-[var(--radius-lg)]"
                style={{ border: '0.5px solid var(--color-border-tertiary, var(--border))' }}
              >
                <div className="overflow-x-auto rounded-[var(--radius-lg)]">
                  <table className="w-full border-collapse text-[13px]">
                    <thead>
                      <tr style={{ background: 'var(--color-background-secondary, var(--muted))' }}>
                        <th className="px-3 py-2.5 text-left text-[12px] font-medium text-muted-foreground" style={{ minWidth: 180 }}>Project</th>
                        <th className="px-3 py-2.5 text-left text-[12px] font-medium text-muted-foreground">Client</th>
                        <th className="px-3 py-2.5 text-left text-[12px] font-medium text-muted-foreground whitespace-nowrap">End date</th>
                        <th className="px-3 py-2.5 text-left text-[12px] font-medium text-muted-foreground whitespace-nowrap">Week releasing</th>
                        <th className="px-3 py-2.5 text-right text-[12px] font-medium text-muted-foreground whitespace-nowrap">Resources releasing</th>
                        <th className="px-3 py-2.5 text-right text-[12px] font-medium text-muted-foreground whitespace-nowrap">Hours releasing</th>
                        <th className="px-3 py-2.5 text-left text-[12px] font-medium text-muted-foreground">Roles breakdown</th>
                        <th className="px-3 py-2.5 text-left text-[12px] font-medium text-muted-foreground">Status</th>
                        <th className="px-3 py-2.5 w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {cohortRows.map((row, idx) => {
                        const rowKey = String(row.projectId);
                        const isExpanded = expandedCohortRows.has(rowKey);
                        const visibleRoles = row.roles.slice(0, 3);
                        const hiddenRoleCount = row.roles.length - visibleRoles.length;
                        return (
                          <React.Fragment key={rowKey}>
                            <tr
                              className="cursor-pointer transition-colors"
                              style={{
                                height: 52,
                                background:
                                  idx % 2 === 0
                                    ? 'var(--background)'
                                    : 'var(--color-background-secondary, var(--muted))',
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLTableRowElement).style.background = 'var(--teal-50)';
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLTableRowElement).style.background =
                                  idx % 2 === 0
                                    ? 'var(--background)'
                                    : 'var(--color-background-secondary, var(--muted))';
                              }}
                              onClick={() => {
                                setExpandedCohortRows((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(rowKey)) next.delete(rowKey);
                                  else next.add(rowKey);
                                  return next;
                                });
                              }}
                            >
                              {/* Project */}
                              <td className="px-3 py-2" style={{ minWidth: 180 }}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="flex items-center justify-center h-7 w-7 rounded-md shrink-0"
                                    style={{ background: 'var(--teal-50)' }}
                                  >
                                    <FolderOpen className="h-3.5 w-3.5 text-teal-500" />
                                  </div>
                                  <span className="font-medium text-[13px] text-foreground truncate">{row.projectName}</span>
                                </div>
                              </td>
                              {/* Client */}
                              <td className="px-3 py-2 text-foreground">{row.clientName}</td>
                              {/* End date */}
                              <td className="px-3 py-2 text-foreground whitespace-nowrap">
                                {format(parseISO(row.endDate), 'dd MMM yyyy')}
                              </td>
                              {/* Week */}
                              <td className="px-3 py-2">
                                <span className="inline-flex items-center h-5 px-2 rounded-full text-[11px] font-semibold bg-primary/10 text-primary">
                                  {row.weekLabel}
                                </span>
                              </td>
                              {/* Resources releasing */}
                              <td className="px-3 py-2 text-right">
                                <span className="text-[var(--accent-teal-900)] dark:text-[var(--accent-teal-500)] font-semibold text-[15px]">
                                  {row.resourcesReleasing}
                                </span>
                              </td>
                              {/* Hours releasing */}
                              <td className="px-3 py-2 text-right text-foreground font-medium">
                                {row.hoursReleasing}h
                              </td>
                              {/* Roles breakdown */}
                              <td className="px-3 py-2 max-w-[260px]">
                                <span className="text-[12px] text-muted-foreground">
                                  {visibleRoles.map((rl) => `${rl.headcount} ${rl.role}`).join(' · ')}
                                  {hiddenRoleCount > 0 && (
                                    <span className="text-primary ml-1">+ {hiddenRoleCount} more role{hiddenRoleCount !== 1 ? 's' : ''}</span>
                                  )}
                                </span>
                              </td>
                              {/* Status */}
                              <td className="px-3 py-2">
                                <CohortEndStatusPill status={row.endStatus} />
                              </td>
                              {/* Expand toggle */}
                              <td className="px-2 py-2 text-muted-foreground">
                                {isExpanded
                                  ? <ChevronUp className="h-4 w-4" />
                                  : <ChevronDown className="h-4 w-4" />}
                              </td>
                            </tr>

                            {/* ── Expanded role breakdown ───────────────── */}
                            {isExpanded && (
                              <tr key={`${rowKey}-expanded`}>
                                <td colSpan={9} className="px-0 py-0">
                                  <div
                                    className="border-t border-b"
                                    style={{ background: 'var(--color-background-secondary, var(--muted))' }}
                                  >
                                    <div className="px-4 py-3">
                                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                                        Role breakdown — {row.projectName}
                                      </p>
                                      <div className="flex flex-col gap-1">
                                        {row.roles.map((rl) => (
                                          <div
                                            key={rl.role}
                                            className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2"
                                          >
                                            <div className="flex-1 min-w-0">
                                              <p className="text-[13px] font-medium text-foreground">{rl.role}</p>
                                              {rl.keySkills.length > 0 && (
                                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                                  Skills: {rl.keySkills.join(', ')}
                                                </p>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-4 shrink-0 text-[12px]">
                                              <span className="text-muted-foreground">
                                                <strong className="text-foreground">{rl.headcount}</strong> headcount
                                              </span>
                                              <span className="text-muted-foreground">
                                                <strong className="text-foreground">{rl.hours}h</strong> available
                                              </span>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 text-[12px] gap-1"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const roleResources = row.resources.filter((r) => (r.role ?? 'Unknown') === rl.role);
                                                  setCohortPreselectedResources(roleResources);
                                                  setBookingModalOpen(true);
                                                }}
                                              >
                                                Assign
                                                <ArrowRight className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* ── Stat cards ────────────────────────────────────────────── */}
            <div className="flex gap-3">
              <StatCard
                value={`${data.summary.avgProjected}%`}
                accent="teal"
                label="Avg projected"
                subLabel="Mean projected % across all visible rows"
              />
              <StatCard
                value={`${data.summary.resourcesOnTarget} of ${data.summary.totalResources}`}
                label="On track"
                subLabel="Resources at or above target"
                isFilterable
                isActive={activeCardFilter === 'on-track'}
                onClick={() => {
                  setActiveCardFilter((prev) => prev === 'on-track' ? null : 'on-track');
                  setPage(1);
                }}
              />
              <StatCard
                value={String(data.summary.nearTargetCount)}
                accent="warning"
                label="Near target"
                subLabel="Within 10pp below target"
                isFilterable
                isActive={activeCardFilter === 'near-target'}
                onClick={() => {
                  setActiveCardFilter((prev) => prev === 'near-target' ? null : 'near-target');
                  setPage(1);
                }}
              />
              <StatCard
                value={String(data.summary.atRiskCount)}
                accent="destructive"
                label="At risk"
                subLabel="Resources > 10pp below target"
                isFilterable
                isActive={activeCardFilter === 'at-risk'}
                onClick={() => {
                  setActiveCardFilter((prev) => prev === 'at-risk' ? null : 'at-risk');
                  setPage(1);
                }}
              />
            </div>

            {/* ── Active card filter pill ───────────────────────────────── */}
            {activeCardFilter && (
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-muted-foreground">Filtered by:</span>
                <button
                  type="button"
                  onClick={() => { setActiveCardFilter(null); setPage(1); }}
                  className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                >
                  {activeCardFilter === 'on-track'    && 'Resources on track'}
                  {activeCardFilter === 'near-target'  && 'Near target'}
                  {activeCardFilter === 'at-risk'      && 'Capacity risk'}
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="shrink-0">
                    <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
                <span className="text-[11px] text-muted-foreground">
                  — showing {cardFilteredRows.length} of {data.rows.length} resources
                </span>
              </div>
            )}

            {/* ── Bar chart ────────────────────────────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)] p-4 flex flex-col gap-3"
              style={{
                background: 'var(--background)',
                border: '0.5px solid var(--color-border-tertiary, var(--border))',
              }}
            >
              {/* Chart toolbar */}
              <div className="flex items-center justify-between gap-3 flex-wrap shrink-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[12px] font-medium text-foreground">
                    {chartDisplayData.length} of {data.chartData.length} resources
                  </span>
                  {data.chartData.length > chartDisplayData.length && (
                    <span className="text-[11px] text-muted-foreground">
                      · top {chartDisplayData.length} shown · full data in table below
                    </span>
                  )}

                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Sort by</span>
                    <Select
                      value={chartSortBy}
                      onValueChange={(v) =>
                        setChartSortBy(
                          v as 'name' | 'booked-desc' | 'booked-asc' | 'projected-desc' | 'variance-desc',
                        )
                      }
                    >
                      <SelectTrigger className="h-7 text-[12px] w-[185px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="booked-desc">Booked % (high → low)</SelectItem>
                        <SelectItem value="booked-asc">Booked % (low → high)</SelectItem>
                        <SelectItem value="projected-desc">Projected % (high → low)</SelectItem>
                        <SelectItem value="variance-desc">Variance (worst first)</SelectItem>
                        <SelectItem value="name">Name (A–Z)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Show</span>
                    <Select
                      value={String(chartShowLimit)}
                      onValueChange={(v) => setChartShowLimit(Number(v))}
                    >
                      <SelectTrigger className="h-7 text-[12px] w-[72px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="-1">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Chart area — responsive, fills full section width */}
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={chartDisplayData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                    barCategoryGap="35%"
                    barGap={2}
                  >
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      tickFormatter={(name: string) =>
                        name.length > 8 ? `${name.slice(0, 7)}…` : name
                      }
                    />
                    <YAxis
                      domain={[0, 120]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                    />
                    <RechartsTooltip
                      content={<ChartTooltip />}
                      cursor={{ fill: 'var(--accent)', opacity: 0.3 }}
                    />
                    <ReferenceLine
                      y={100}
                      stroke={GREY_DASHED()}
                      strokeDasharray="4 3"
                      strokeWidth={1.5}
                    />
                    <ReferenceLine
                      y={chartDisplayData[0]?.target ?? 75}
                      stroke={TEAL()}
                      strokeDasharray="4 3"
                      strokeWidth={1.5}
                    />
                    <Bar dataKey="booked" name="booked" stackId="forecast" radius={[0, 0, 0, 0]} maxBarSize={22}>
                      {chartDisplayData.map((entry, idx) => (
                        <Cell key={idx} fill={resolveBarBase(entry)} fillOpacity={barOpacityBooked()} />
                      ))}
                    </Bar>
                    <Bar dataKey="planned" name="planned" stackId="forecast" radius={[3, 3, 0, 0]} maxBarSize={22}>
                      {chartDisplayData.map((entry, idx) => (
                        <Cell key={idx} fill={resolveBarBase(entry)} fillOpacity={barOpacityPlanned()} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 px-1">
                <LegendSwatch colour={TEAL()}        label="On target" />
                <LegendSwatch colour={AMBER()}       label="Near target (within 10pp)" />
                <LegendSwatch colour={RED()}         label="At risk (> 10pp below target)" />
                <LegendSwatch colour={TEAL()}        label="Target line"      dashed />
                <LegendSwatch colour={GREY_DASHED()} label="Capacity limit"   dashed />
              </div>
            </div>

            {/* ── Data table ───────────────────────────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)]"
              style={{ border: '0.5px solid var(--color-border-tertiary, var(--border))' }}
            >
              <div className="overflow-x-auto rounded-[var(--radius-lg)]">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr style={{ background: 'var(--color-background-secondary, var(--muted))' }}>
                      <SortHeader
                        label={
                          viewBy === 'individual' ? 'Resource'
                          : viewBy === 'planning-group' ? 'Team'
                          : viewBy === 'department' ? 'Department'
                          : viewBy === 'role' ? 'Role'
                          : 'Location'
                        }
                        col="name"
                        sortCol={sortCol}
                        sortDir={sortDir}
                        onSort={handleSort}
                        minWidth={200}
                      />
                      {visibleForecastCols.has('department') && <SortHeader label="Department"    col="department" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                      {visibleForecastCols.has('target') && <SortHeader label="Target %"      col="target"     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleForecastCols.has('booked') && <SortHeader label="Booked %"      col="booked"     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleForecastCols.has('planned') && <SortHeader label="Planned %"    col="planned"    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleForecastCols.has('projected') && <SortHeader label="Projected %"   col="projected"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleForecastCols.has('variance') && <SortHeader label="Variance"      col="variance"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleForecastCols.has('bookedh') && <SortHeader label="Booked hrs"    col="bookedh"    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleForecastCols.has('plannedh') && <SortHeader label="Planned hrs"   col="plannedh"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleForecastCols.has('available') && <SortHeader label="Available hrs" col="available"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">
                        Forecast status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, idx) => (
                      <tr
                        key={row.id}
                        onClick={() => setDrawerRow(row)}
                        className="cursor-pointer transition-colors"
                        style={{
                          height: 48,
                          background:
                            idx % 2 === 0
                              ? 'var(--background)'
                              : 'var(--color-background-secondary, var(--muted))',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background = 'var(--teal-50)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background =
                            idx % 2 === 0
                              ? 'var(--background)'
                              : 'var(--color-background-secondary, var(--muted))';
                        }}
                      >
                        {/* Resource */}
                        <td className="px-3 py-2" style={{ minWidth: 200 }}>
                          <div className="flex items-center gap-2.5">
                            <Avatar className="h-7 w-7 shrink-0">
                              <AvatarImage src={row.photoUrl} alt={row.name} />
                              <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                                {row.initials}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-medium text-[13px] text-foreground truncate leading-tight flex items-center gap-1.5">
                                {row.name}
                                {row.hasRiskFlag && (
                                  <AlertTriangle className="h-3 w-3 text-warning-500 shrink-0" aria-label="Capacity risk" />
                                )}
                              </p>
                              <p className="text-[11px] text-muted-foreground truncate leading-tight">
                                {row.role}
                              </p>
                            </div>
                          </div>
                        </td>
                        {visibleForecastCols.has('department') && <td className="px-3 py-2 text-foreground whitespace-nowrap">{row.department}</td>}
                        {visibleForecastCols.has('target') && <td className="px-3 py-2 text-right text-foreground">{row.targetPct}%</td>}
                        {visibleForecastCols.has('booked') && <td className="px-3 py-2 text-right"><PctCell pct={row.bookedPct} targetPct={row.targetPct} /></td>}
                        {visibleForecastCols.has('planned') && <td className="px-3 py-2 text-right text-muted-foreground">{row.plannedPct}%</td>}
                        {visibleForecastCols.has('projected') && <td className="px-3 py-2 text-right"><PctCell pct={row.projectedPct} targetPct={row.targetPct} /></td>}
                        {visibleForecastCols.has('variance') && <td className="px-3 py-2 text-right"><VarianceCell variance={row.variancePct} /></td>}
                        {visibleForecastCols.has('bookedh') && <td className="px-3 py-2 text-right text-foreground">{Math.round(row.bookedHours)}h</td>}
                        {visibleForecastCols.has('plannedh') && <td className="px-3 py-2 text-right text-muted-foreground">{Math.round(row.plannedHours)}h</td>}
                        {visibleForecastCols.has('available') && <td className="px-3 py-2 text-right text-foreground">{Math.round(row.availableHours)}h</td>}
                        <td className="px-3 py-2">
                          <StatusPill status={row.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  {/* Footer totals */}
                  {footerTotals && (
                    <tfoot>
                      <tr
                        style={{
                          background: 'var(--color-background-secondary, var(--muted))',
                          borderTop: '0.5px solid var(--color-border-primary, var(--border))',
                        }}
                      >
                        <td className="px-3 py-2.5 font-medium text-[13px] text-foreground">
                          Total / average
                        </td>
                        {visibleForecastCols.has('department') && <td />}
                        {visibleForecastCols.has('target') && (
                          <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                            {footerTotals.targetAvg}%
                          </td>
                        )}
                        {visibleForecastCols.has('booked') && (
                          <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                            <PctCell pct={footerTotals.bookedAvg} targetPct={footerTotals.targetAvg} />
                          </td>
                        )}
                        {visibleForecastCols.has('planned') && (
                          <td className="px-3 py-2.5 text-right font-medium text-[13px] text-muted-foreground">
                            {footerTotals.plannedAvg}%
                          </td>
                        )}
                        {visibleForecastCols.has('projected') && (
                          <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                            <PctCell pct={footerTotals.projectedAvg} targetPct={footerTotals.targetAvg} />
                          </td>
                        )}
                        {visibleForecastCols.has('variance') && (
                          <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                            <VarianceCell variance={footerTotals.varianceAvg} />
                          </td>
                        )}
                        {visibleForecastCols.has('bookedh') && (
                          <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                            {footerTotals.bookedSum}h
                          </td>
                        )}
                        {visibleForecastCols.has('plannedh') && (
                          <td className="px-3 py-2.5 text-right font-medium text-[13px] text-muted-foreground">
                            {footerTotals.plannedSum}h
                          </td>
                        )}
                        {visibleForecastCols.has('available') && (
                          <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                            {footerTotals.availableSum}h
                          </td>
                        )}
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* ── Pagination ────────────────────────────────────────────── */}
            <div className="flex items-center justify-between shrink-0 pb-2">
              <p className="text-[13px] text-muted-foreground">
                Showing {Math.min((page - 1) * pageSize + 1, sortedRows.length)}–
                {Math.min(page * pageSize, sortedRows.length)} of {sortedRows.length} resources
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 text-[13px]"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <span className="text-[13px] text-muted-foreground px-1">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-8 text-[13px]"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[13px] text-muted-foreground">Rows per page:</span>
                <Select
                  value={pageSize >= 999999 ? 'all' : String(pageSize)}
                  onValueChange={(v) => { setPageSize(v === 'all' ? 999999 : Number(v)); setPage(1); }}
                >
                  <SelectTrigger className="h-8 w-20 text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Forecast detail drawer ────────────────────────────────────────── */}
      <ForecastDrawer
        row={drawerRow}
        open={drawerRow !== null}
        onClose={() => setDrawerRow(null)}
      />

      {/* ── Add Booking modal — cohort pre-populated ──────────────────────── */}
      {bookingModalOpen && (
        <ComprehensiveBookingModal
          open={bookingModalOpen}
          onOpenChange={(o) => {
            setBookingModalOpen(o);
            if (!o) setCohortPreselectedResources([]);
          }}
          onSave={async (booking) => {
            await addBooking(booking);
            setBookingModalOpen(false);
            setCohortPreselectedResources([]);
            toast.success('Booking saved');
          }}
          onUpdate={async (id, booking) => { await updateBooking(id as string, booking); }}
          onDelete={(booking) => { deleteBooking(booking.id); setBookingModalOpen(false); }}
          resources={resources}
          projectDetails={projectDetails}
          existingBookings={allBookings}
          preselectedResource={cohortPreselectedResources[0]}
          mode="create"
        />
      )}
    </div>
  );
}
